import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { useAppThunkDispatch } from 'state/store';

import {
  fetchTeamTasks,
  selectFetchTeamTasksStatus,
  selectTeamTasks,
  setFilters,
  setSearchFilter,
  selectTeamTasksTableFilters,
  selectTeamTasksSorting,
  setSorting,
  selectCanFetchMoreTeamTasks,
  selectTeamTasksTotalCount,
} from 'state/TasksList/teamTasks/teamTasksSlice';

import {
  fetchTeamTasks as fetchTeamTasksNormalized,
  selectFetchTeamTasksStatus as selectFetchTeamTasksStatusNormalized,
  selectTeamTasks as selectTeamTasksNormalized,
  setFilters as setFiltersNormalized,
  setSearchFilter as setSearchFilterNormalized,
  selectTeamTasksSorting as selectTeamTasksSortingNormalized,
  selectTeamTasksTableFilters as selectTeamTasksTableFiltersNormalized,
  selectCanFetchMoreTeamTasks as selectCanFetchMoreTeamTasksNormalized,
  selectTeamTasksTotalCount as selectTeamTasksTotalCountNormalized,
  setSorting as setSortingNormalized,
  FetchTasksListParams,
} from 'state/normalized/tasksList/teamTasks/teamTasksSlice';
import {
  setShouldRefreshTasks,
  selectShouldRefreshTasks,
} from 'state/TasksList/allTasks/allTasksSlice';
import {
  selectShouldRefreshTasks as selectShouldRefreshTasksNormalized,
  setShouldRefreshTasks as setShouldRefreshTasksNormalized,
} from 'state/normalized/tasksList/allTasks/allTasksSlice';
import {
  TaskListFilters,
  TaskWithAssigneesAndProject,
} from 'types/store/tasksList';
import { fetchFilterSettingByType } from 'state/Settings/Filters/FiltersSlice';
import { CENTRALIZED_TASKS_TABLE_TABS } from 'utils/constants/centralizedTasks';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';

interface UseTeamTasksProps {
  useNormalizedState?: boolean;
}

export const useTeamTasks = ({
  useNormalizedState = false,
}: UseTeamTasksProps) => {
  const dispatch = useAppThunkDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const shouldRefreshTasks = useSelector(
    useNormalizedState
      ? selectShouldRefreshTasksNormalized
      : selectShouldRefreshTasks
  );
  const fetchTeamTasksStatus = useSelector(
    useNormalizedState
      ? selectFetchTeamTasksStatusNormalized
      : selectFetchTeamTasksStatus
  );
  const teamTasks = useSelector<RootState, TaskWithAssigneesAndProject[]>(
    useNormalizedState ? selectTeamTasksNormalized : selectTeamTasks
  );
  const teamTasksSorting = useSelector(
    useNormalizedState
      ? selectTeamTasksSortingNormalized
      : selectTeamTasksSorting
  );
  const teamTasksTableFilters = useSelector(
    useNormalizedState
      ? selectTeamTasksTableFiltersNormalized
      : selectTeamTasksTableFilters
  );
  const canFetchMoreTasks = useSelector(
    useNormalizedState
      ? selectCanFetchMoreTeamTasksNormalized
      : selectCanFetchMoreTeamTasks
  );
  const totalCount = useSelector(
    useNormalizedState
      ? selectTeamTasksTotalCountNormalized
      : selectTeamTasksTotalCount
  );

  const setShouldRefreshTasksAction = useMemo(
    () =>
      useNormalizedState
        ? setShouldRefreshTasksNormalized
        : setShouldRefreshTasks,
    [useNormalizedState]
  );

  const prevFetchTeamTasksStatus = useRef<Status>(fetchTeamTasksStatus);

  const dispatchFetchTeamTasks = useCallback(
    ({ fetchNext = false, includeTotalCount = true }: FetchTasksListParams) => {
      const fetchTeamTasksAction = useNormalizedState
        ? fetchTeamTasksNormalized
        : fetchTeamTasks;
      dispatch(fetchTeamTasksAction({ fetchNext, includeTotalCount }));
    },
    [dispatch, useNormalizedState]
  );

  const init = useCallback(async () => {
    await dispatch(
      fetchFilterSettingByType(CENTRALIZED_TASKS_TABLE_TABS.TEAM_TASKS)
    );
    dispatchFetchTeamTasks({
      fetchNext: false,
      includeTotalCount: true,
    });
  }, [dispatch, dispatchFetchTeamTasks]);

  const refreshTasks = useCallback(() => {
    dispatch(setShouldRefreshTasksAction(true));
    init();
  }, [dispatch, init, setShouldRefreshTasksAction]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (shouldRefreshTasks) {
      dispatch(setShouldRefreshTasksAction(false));
      init();
    }
  }, [shouldRefreshTasks, dispatch, init, setShouldRefreshTasksAction]);

  const hasError = fetchTeamTasksStatus === SLICE_STATUS.FAILED;

  /**
   * Clear the initial loading state once the fetch reaches a terminal status.
   * FAILED must be treated as terminal alongside IDLE: teamTasksSlice sets
   * FAILED (never IDLE) when fetchTeamTasks rejects, so keying only off IDLE
   * leaves TeamTasks rendering its skeleton loader forever on any request error.
   */
  useEffect(() => {
    if (fetchTeamTasksStatus !== prevFetchTeamTasksStatus.current) {
      prevFetchTeamTasksStatus.current = fetchTeamTasksStatus;
      if (
        isLoading &&
        (fetchTeamTasksStatus === SLICE_STATUS.IDLE ||
          fetchTeamTasksStatus === SLICE_STATUS.FAILED)
      ) {
        setIsLoading(false);
      }
    }
  }, [fetchTeamTasksStatus, isLoading]);

  const handleUpdateFilters = useCallback(
    (updatedFilters: TaskListFilters) => {
      const setFiltersAction = useNormalizedState
        ? setFiltersNormalized
        : setFilters;
      dispatch(setFiltersAction(updatedFilters));
      dispatchFetchTeamTasks({
        fetchNext: false,
        includeTotalCount: true,
      });
    },
    [dispatch, dispatchFetchTeamTasks, useNormalizedState]
  );

  const handleSearchFilterChange = useCallback(
    (value: string) => {
      const setSearchFilterAction = useNormalizedState
        ? setSearchFilterNormalized
        : setSearchFilter;
      dispatch(setSearchFilterAction(value));
      dispatchFetchTeamTasks({
        fetchNext: false,
        includeTotalCount: true,
      });
    },
    [dispatch, dispatchFetchTeamTasks, useNormalizedState]
  );

  const handleTableSort = useCallback(
    (sortBy: string, order: string) => {
      const setSortingAction = useNormalizedState
        ? setSortingNormalized
        : setSorting;
      dispatch(setSortingAction({ sortBy, order }));
      dispatchFetchTeamTasks({
        fetchNext: false,
        includeTotalCount: false,
      });
    },
    [dispatch, dispatchFetchTeamTasks, useNormalizedState]
  );

  const handleFetchMoreTasks = useCallback(() => {
    dispatchFetchTeamTasks({
      fetchNext: true,
      includeTotalCount: false,
    });
  }, [dispatchFetchTeamTasks]);

  return useMemo(() => {
    return {
      isLoading,
      hasError,
      teamTasks,
      teamTasksSorting,
      teamTasksTableFilters,
      canFetchMoreTasks,
      totalCount,
      refreshTasks,
      handleUpdateFilters,
      handleSearchFilterChange,
      handleTableSort,
      handleFetchMoreTasks,
    };
  }, [
    isLoading,
    hasError,
    teamTasks,
    teamTasksSorting,
    teamTasksTableFilters,
    canFetchMoreTasks,
    totalCount,
    refreshTasks,
    handleUpdateFilters,
    handleSearchFilterChange,
    handleTableSort,
    handleFetchMoreTasks,
  ]);
};
